import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DataTable } from "../../../components/table";
import type { EGiftCardFilter, EGiftCardListItemResponse } from "../types/eGiftCard.types";
import { buildEGiftCardListColumns } from "./EGiftCardListTableColumns";
import { useGetMerchantBrandOptions } from "../../common";

type EGiftCardTableProps = {
  loading?: boolean | null;
  error?: string | null;
  eGiftCards: EGiftCardListItemResponse[];
  filter: EGiftCardFilter;
  setFilter: React.Dispatch<React.SetStateAction<EGiftCardFilter>>;
  onRetry?: () => void;
  onRestock?: (eGiftCard: EGiftCardListItemResponse) => void;
  onDelete?: (eGiftCard: EGiftCardListItemResponse) => void;
  onEdit?: (eGiftCard: EGiftCardListItemResponse) => void;
  onRowClick?: (eGiftCard: EGiftCardListItemResponse) => void;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
};

const EGiftCardTable = ({
  loading = false,
  error = null,
  eGiftCards,
  filter,
  setFilter,
  onRetry,
  onRestock,
  onDelete,
  onEdit,
  onRowClick,
  page,
  totalPages,
  onPageChange,
}: EGiftCardTableProps) => {
  const { t } = useTranslation();

  const { merchantBrandOptions, fetchMerchantBrandOptions } = useGetMerchantBrandOptions();

  useEffect(() => {
    void fetchMerchantBrandOptions();
  }, [fetchMerchantBrandOptions]);

  const updateFilter = useCallback(
    (key: keyof EGiftCardFilter, value: EGiftCardFilter[keyof EGiftCardFilter]) => {
      setFilter((current) => ({ ...current, [key]: value }));
    },
    [setFilter],
  );

  const columns = useMemo(
    () =>
      buildEGiftCardListColumns({
        t,
        filters: filter,
        updateFilter,
        onRestock,
        onEdit,
        onDelete,
        merchantBrandOptions,
      }),
    [t, filter, updateFilter, onRestock, onEdit, onDelete, merchantBrandOptions],
  );

  return (
    <DataTable
      columns={columns}
      dataSource={eGiftCards}
      rowKey="id"
      loading={Boolean(loading)}
      loadingLabel={t("eGiftCard.loading_e_gift_cards")}
      error={error}
      onRetry={onRetry}
      emptyLabel={t("eGiftCard.no_e_gift_cards_found")}
      containerClassName="max-h-[calc(100vh-24rem)] overflow-auto"
      tableClassName="min-w-max"
      onRowClick={onRowClick}
      pagination={{ page, totalPages, onPageChange }}
    />
  );
};

export default EGiftCardTable;
